
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Team } from '../types';

export function useTeamUpdate() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      if (!user) throw new Error('User not authenticated');
      
      // First check if user is admin or superadmin
      const { data: roleData, error: roleError } = await supabase
        .rpc('get_team_member_role', { 
          team_id: id,
          user_id: user.id
        });
        
      if (roleError) throw roleError;
        
      if (roleData !== 'admin' && roleData !== 'superadmin') {
        throw new Error('You do not have permission to update this team');
      }
      
      const { data, error } = await supabase
        .from('teams')
        .update({ name })
        .eq('id', id)
        .select('*')
        .single();
        
      if (error) throw error;
      return data as Team;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast({
        title: "Team updated",
        description: "The team has been updated successfully.",
      });
    },
    onError: (error: any) => {
      console.error('Update team error:', error);
      toast({
        title: "Error updating team",
        description: error.message || "There was an error updating the team.",
        variant: "destructive",
      });
    }
  });
}
